import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { Product } from 'types/graphql-overrides';
import { parseProduct, getPotencyStrings } from 'src/utils/helpers/product';
import { withModalControlsHook, ModalComponentProps } from 'src/components/modals/with-modal-controls-hook';
import { ModalWrapper } from 'shared/modals/v2-wrapper';
import { ModalContainer, BottomDrawer, HorizontalDivider } from 'src/components/core';
import { ModalExitIcon } from 'shared/modals/v2-container';
import { DesktopOnly, MobileOnly } from 'shared/components';
import { heading3 } from 'src/components/core/typography';
import { useQuickAddToCart } from 'src/components/product-carousel/use-quick-add-to-cart';
import { buttonStyles } from 'src/components/product-card-v2/card-action-button';
import { OriginalProduct } from './use-buy-it-again-carousel';
import { StrainBadge, PotencyText } from './buy-it-again-shared-styles';

export type BuyItAgainSubstituteModalData = {
  originalProduct: OriginalProduct;
  substituteCandidates: Product[];
  trackerSource: string | null;
};

type ContentProps = {
  data: BuyItAgainSubstituteModalData;
  onClose: () => void;
};

export function ModalContent({ data, onClose }: ContentProps): JSX.Element {
  const { originalProduct, substituteCandidates, trackerSource } = data;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { handleAddToCart } = useQuickAddToCart();

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const update = (): void => {
      setCanScrollLeft(el.scrollLeft > 0);
      setCanScrollRight(Math.ceil(el.scrollLeft) < el.scrollWidth - el.clientWidth);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [substituteCandidates]);

  const productName = originalProduct.Name ?? '';
  const brandName = originalProduct.brandName ?? '';
  const imageUrl = originalProduct.Image ?? null;
  const { strainType } = originalProduct;
  const { THC: THCString, CBD: CBDString, TAC: TACString } = getPotencyStrings(originalProduct);
  const potencyLines: string[] = [];
  if (TACString) potencyLines.push(`TAC: ${TACString}`);
  if (THCString) potencyLines.push(`THC: ${THCString}`);
  if (CBDString) potencyLines.push(`CBD: ${CBDString}`);

  const handleAddSelectedToCart = (): void => {
    const selected = substituteCandidates[selectedIndex];
    if (!selected) return;
    onClose();
    void handleAddToCart(selected, trackerSource);
  };

  return (
    <Content>
      <OosSection>
        <OosImageWrapper>
          {imageUrl ? <OosImage src={imageUrl} alt={productName} /> : <OosImagePlaceholder />}
        </OosImageWrapper>
        <OosDetails>
          <OosBadge>Out of stock</OosBadge>
          <OosName>{productName}</OosName>
          {brandName && <OosBrand>{brandName}</OosBrand>}
          {(strainType || potencyLines.length > 0) && (
            <OosStrainDetails>
              {strainType && <StrainBadge>{strainType}</StrainBadge>}
              {potencyLines.map((line) => (
                <PotencyText key={line}>{line}</PotencyText>
              ))}
            </OosStrainDetails>
          )}
        </OosDetails>
      </OosSection>

      <HorizontalDivider my='16px' />

      <SubstitutesSection>
        <SubstitutesHeading>Recommended substitutes</SubstitutesHeading>
        {substituteCandidates.length === 0 ? (
          <EmptyState>We couldn&apos;t find any substitutes for this product right now. Check back later.</EmptyState>
        ) : null}
        <CandidatesScrollWrapper>
          <CandidatesScroll ref={scrollRef}>
            {substituteCandidates.map((candidate, i) => {
              const parsed = parseProduct(candidate);
              const { THC: cTHCString, CBD: cCBDString, TAC: cTACString } = getPotencyStrings(parsed);
              const candidatePotencyLines: string[] = [];
              if (cTACString) candidatePotencyLines.push(`TAC: ${cTACString}`);
              if (cTHCString) candidatePotencyLines.push(`THC: ${cTHCString}`);
              if (cCBDString) candidatePotencyLines.push(`CBD: ${cCBDString}`);
              const firstPrice = parsed.recPrices?.[0] ?? parsed.Prices?.[0];
              const displayPrice = firstPrice != null ? `$${Number(firstPrice).toFixed(2)}` : null;

              return (
                <CandidateCard
                  key={candidate.id ?? String(i)}
                  $selected={i === selectedIndex}
                  onClick={() => setSelectedIndex(i)}
                  role='button'
                  tabIndex={0}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setSelectedIndex(i)}
                  aria-pressed={i === selectedIndex}
                  aria-label={parsed.name ?? parsed.Name ?? undefined}
                >
                  <CandidateImageWrapper>
                    {failedImages.has(candidate.id ?? String(i)) ? (
                      <CandidateImagePlaceholder />
                    ) : (
                      <CandidateImage
                        src={parsed.Image ?? ''}
                        alt={parsed.name ?? parsed.Name ?? ''}
                        onError={() => setFailedImages((prev) => new Set(prev).add(candidate.id ?? String(i)))}
                      />
                    )}
                  </CandidateImageWrapper>
                  <CandidateText>
                    <CandidateName>{parsed.name ?? parsed.Name}</CandidateName>
                    {parsed.brandName && <CandidateBrand>{parsed.brandName}</CandidateBrand>}
                    {(parsed.strainType || candidatePotencyLines.length > 0) && (
                      <CandidateStrainDetails>
                        {parsed.strainType && <StrainBadge>{parsed.strainType}</StrainBadge>}
                        {candidatePotencyLines.map((line) => (
                          <PotencyText key={line}>{line}</PotencyText>
                        ))}
                      </CandidateStrainDetails>
                    )}
                    {displayPrice && <CandidatePrice>{displayPrice}</CandidatePrice>}
                  </CandidateText>
                </CandidateCard>
              );
            })}
          </CandidatesScroll>
          {canScrollLeft && <ScrollFadeLeft />}
          {canScrollRight && <ScrollFadeRight />}
        </CandidatesScrollWrapper>
      </SubstitutesSection>

      <Actions>
        {substituteCandidates.length > 0 && (
          <AddToCartButton onClick={handleAddSelectedToCart}>Add to cart</AddToCartButton>
        )}
        <CancelButton onClick={onClose}>{substituteCandidates.length > 0 ? 'Cancel' : 'Close'}</CancelButton>
      </Actions>
    </Content>
  );
}

const BuyItAgainSubstituteModalInner = ({
  onClose,
  containerProps,
  data,
}: ModalComponentProps<BuyItAgainSubstituteModalData>): JSX.Element => (
  <>
    <DesktopOnly mediaQuery='sm'>
      <ModalWrapper onClose={onClose} open>
        <ModalContainer width='min(534px, calc(100vw - 64px))' padding={32} {...containerProps}>
          <ModalExitIcon onClick={onClose} />
          <ModalHeading>Item Unavailable</ModalHeading>
          <ModalContent data={data} onClose={onClose} />
        </ModalContainer>
      </ModalWrapper>
    </DesktopOnly>
    <MobileOnly mediaQuery='xs'>
      <BottomDrawer open onClose={onClose} heading='Item Unavailable'>
        <ModalContent data={data} onClose={onClose} />
      </BottomDrawer>
    </MobileOnly>
  </>
);

export const [
  BuyItAgainSubstituteModalController,
  useBuyItAgainSubstituteModal,
] = withModalControlsHook<BuyItAgainSubstituteModalData>(BuyItAgainSubstituteModalInner);

// ─── Styles ──────────────────────────────────────────────────────────────────

const ModalHeading = styled.h3`
  ${heading3}
  margin-bottom: 20px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  padding-top: 20px;
`;

const OosSection = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  padding-bottom: 16px;
`;

const OosImageWrapper = styled.div`
  flex-shrink: 0;
  height: 64px;
  position: relative;
  width: 64px;
`;

const OosImage = styled.img`
  height: 100%;
  object-fit: contain;
  width: 100%;
`;

const OosImagePlaceholder = styled.div`
  background: ${({ theme }) => theme.colors.grey[90]};
  border-radius: 4px;
  height: 100%;
  width: 100%;
`;

const OosBadge = styled.div`
  background: rgba(0, 0, 0, 0.75);
  border-radius: 4px;
  color: #fff;
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
  padding: 4px 6px;
  text-transform: uppercase;
`;

const OosDetails = styled.div`
  flex: 1;
  min-width: 0;
  text-align: left;
`;

const OosName = styled.p`
  color: #121516;
  font-size: 14px;
  font-weight: 600;
  line-height: ${16 / 14};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OosBrand = styled.p`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  color: #646d72;
  display: -webkit-box;
  font-size: 12px;
  line-height: 1;
  margin-top: 4px;
  overflow: hidden;
`;

const OosStrainDetails = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
`;

const SubstitutesSection = styled.div`
  margin-bottom: 20px;
`;

const EmptyState = styled.p`
  color: ${({ theme }) => theme.colors.grey[45]};
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 12px;
  text-align: left;
`;

const SubstitutesHeading = styled.h4`
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 12px;
  text-align: left;
`;

const CandidatesScrollWrapper = styled.div`
  position: relative;
`;

const CandidatesScroll = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 4px;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const scrollFadeBase = `
  bottom: 0;
  pointer-events: none;
  position: absolute;
  top: 0;
  width: 48px;
`;

const ScrollFadeRight = styled.div`
  ${scrollFadeBase}
  background: linear-gradient(to right, transparent, var(--dcm-color-body-bg, #fff) 85%);
  right: 0;
`;

const ScrollFadeLeft = styled.div`
  ${scrollFadeBase}
  background: linear-gradient(to left, transparent, var(--dcm-color-body-bg, #fff) 85%);
  left: 0;
`;

const CandidateCard = styled.div<{ $selected: boolean }>`
  background: #fff;
  border: 2px solid
    ${({ $selected, theme }) => ($selected ? theme.customized.colors.buttonsLinks : theme.colors.grey[90])};
  border-radius: ${({ theme }) => theme.customized.radius.lg};
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  transition: border-color 0.15s;

  /* Mobile: horizontal row, wide enough that the next card peeks */
  align-items: center;
  flex-direction: row;
  gap: 12px;
  padding: 10px 12px;
  width: min(300px, calc(100vw - 80px));

  ${({ theme }) => theme.breakpoints.up('sm')} {
    /* Desktop: vertical column, grows to fill the row when fewer than 3 candidates. Capped so a
       single candidate doesn't stretch to the full row width (2 candidates still fill exactly,
       since 230px < the 240px cap). */
    align-items: flex-start;
    flex: 1 1 150px;
    flex-direction: column;
    gap: 0;
    max-width: 240px;
    padding: 10px;
  }

  &:hover {
    border-color: ${({ theme }) => theme.customized.colors.buttonsLinks};
  }
`;

const CandidateImageWrapper = styled.div`
  flex-shrink: 0;
  position: relative;

  /* Mobile */
  height: 76px;
  width: 76px;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    height: 100px;
    margin-bottom: 8px;
    width: 100%;
  }
`;

const CandidateImagePlaceholder = styled.div`
  background: ${({ theme }) => theme.colors.grey[90]};
  border-radius: 4px;
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
`;

const CandidateImage = styled.img`
  display: block;
  height: 100%;
  object-fit: contain;
  width: 100%;
`;

const CandidateText = styled.div`
  flex: 1;
  min-width: 0;
  text-align: left;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    flex: unset;
    width: 100%;
  }
`;

const CandidateName = styled.p`
  color: #121516;
  font-size: 14px;
  font-weight: 600;
  line-height: ${16 / 14};
`;

const CandidateBrand = styled.p`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  color: #646d72;
  display: -webkit-box;
  font-size: 12px;
  line-height: 1;
  margin-top: 4px;
  overflow: hidden;
`;

const CandidateStrainDetails = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    margin-top: 16px;
  }
`;

const CandidatePrice = styled.p`
  color: #2c3236;
  font-size: 20px;
  font-weight: 600;
  line-height: 1;
  margin: 10px 0 0;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    margin: 16px 0 8px;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AddToCartButton = styled.button`
  ${buttonStyles}
  font-size: 15px;
  line-height: 17px;
  padding: 17px 22px;
`;

const CancelButton = styled.button`
  appearance: none;
  background: transparent;
  border: 1.5px solid ${({ theme }) => theme.customized.colors.buttonsLinks};
  border-radius: ${({ theme }) => theme.customized.radius.buttons};
  color: ${({ theme }) => theme.customized.colors.buttonsLinks};
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  line-height: 17px;
  padding: 17px 22px;
  text-align: center;
  text-transform: ${({ theme }) => theme.customized.textTransforms.buttons};
  transition: all 0.2s;
  width: 100%;

  &:hover {
    background: ${({ theme }) => theme.customized.colors.buttonsLinks}14;
  }
`;
